"use client"

import Image from "next/image"
import Link from "next/link"
import { useState } from "react"

import { CancelIcon } from "@modules/navigation/icons/CancelIcon"

const TopBanner = ({ rating }: { rating: number }) => {
  const [isVisible, setIsVisible] = useState(true)

  if (!isVisible) {
    return null
  }

  return (
    <>
      <div className="relative hidden items-center justify-center bg-[#CDE6FE] py-3 md:flex">
        <p className="font-nunito text-[16px] leading-[28px] tracking-[-2%]">
          <a href="https://a.nelo.mx/LkxqCvLUJRb" target="_blank" rel="noopener noreferrer" className="font-[600] underline">
            ¿No tienes la app?
          </a>{" "}
          Descárgala y descubre el crédito a tu medida, como debe ser. 💙
        </p>
        <button className="absolute right-4 top-1/2 -translate-y-1/2" onClick={() => setIsVisible(false)}>
          <CancelIcon />
        </button>
      </div>
      <div
        className="relative z-40 hidden h-[72px] items-center justify-between bg-[#444444]/60 px-4 py-3 tiny:flex md:hidden"
        style={{
          boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.25)",
        }}
      >
        <button className="absolute left-4" onClick={() => setIsVisible(false)}>
          <CancelIcon />
        </button>
        <div className="ml-[30px] flex items-center">
          <div className="mr-4">
            <Image
              //
              alt="Nelo app icon"
              src="/images/nelo-mobile-icon.png"
              width={55}
              height={55}
              className="h-[55px] w-[55px]"
            />
          </div>
          <div>
            <p className="mb-[2px] text-[16px] font-bold leading-[100%] tracking-normal text-white">Descarga la app Nelo</p>
            <p className="text-[11px] font-semibold leading-[14px] tracking-normal text-white">
              Compra en línea, paga servicios
              <br />y haz recargas a quincenas.
            </p>
            <div className="flex items-center gap-1 font-nunito text-[9px] text-white">
              <span>{rating}</span> <StarRating rating={rating} />
            </div>
          </div>
        </div>
        <Link
          href="https://a.nelo.mx/LkxqCvLUJRb"
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-[100px] bg-white px-[10px] py-[14px] text-[14px] font-bold leading-[100%] tracking-normal text-nelo-blue"
        >
          OBTENER
        </Link>
      </div>
    </>
  )
}

export default TopBanner

const StarIcon = ({ fillPercentage = 100, index }: { fillPercentage?: number; index: number }) => {
  const gradientId = `partialFill-${index}`
  return (
    <svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset={`${fillPercentage}%`} stopColor="#FFCF56" />
          <stop offset={`${fillPercentage}%`} stopColor="transparent" />
        </linearGradient>
      </defs>
      <path
        d="M4.80979 0.58541C4.86966 0.401148 5.13034 0.401148 5.19021 0.58541L6.07767 3.31672C6.10444 3.39912 6.18123 3.45492 6.26788 3.45492H9.13975C9.33349 3.45492 9.41405 3.70284 9.2573 3.81672L6.93391 5.50476C6.86382 5.55569 6.83448 5.64596 6.86126 5.72837L7.74871 8.45967C7.80859 8.64394 7.59769 8.79716 7.44095 8.68328L5.11756 6.99524C5.04746 6.94431 4.95254 6.94431 4.88244 6.99524L2.55905 8.68328C2.40231 8.79716 2.19141 8.64394 2.25129 8.45967L3.13874 5.72837C3.16552 5.64596 3.13618 5.55569 3.06609 5.50476L0.742697 3.81672C0.585954 3.70284 0.66651 3.45492 0.860254 3.45492H3.73212C3.81877 3.45492 3.89556 3.39912 3.92233 3.31672L4.80979 0.58541Z"
        fill="white"
      />
      <path
        d="M4.80979 0.58541C4.86966 0.401148 5.13034 0.401148 5.19021 0.58541L6.07767 3.31672C6.10444 3.39912 6.18123 3.45492 6.26788 3.45492H9.13975C9.33349 3.45492 9.41405 3.70284 9.2573 3.81672L6.93391 5.50476C6.86382 5.55569 6.83448 5.64596 6.86126 5.72837L7.74871 8.45967C7.80859 8.64394 7.59769 8.79716 7.44095 8.68328L5.11756 6.99524C5.04746 6.94431 4.95254 6.94431 4.88244 6.99524L2.55905 8.68328C2.40231 8.79716 2.19141 8.64394 2.25129 8.45967L3.13874 5.72837C3.16552 5.64596 3.13618 5.55569 3.06609 5.50476L0.742697 3.81672C0.585954 3.70284 0.66651 3.45492 0.860254 3.45492H3.73212C3.81877 3.45492 3.89556 3.39912 3.92233 3.31672L4.80979 0.58541Z"
        fill={fillPercentage === 100 ? "#FFCF56" : `url(#${gradientId})`}
      />
    </svg>
  )
}

const StarRating = ({ rating }: { rating: number }) => {
  const fullStars = Math.floor(rating)
  const partialStar = rating % 1
  const emptyStars = 5 - Math.ceil(rating)

  return (
    <div className="flex items-center">
      {[...Array(fullStars)].map((_, index) => (
        <StarIcon key={`full-${index}`} index={index} />
      ))}
      {partialStar > 0 && <StarIcon key="partial" index={fullStars} fillPercentage={partialStar * 100} />}
      {[...Array(emptyStars)].map((_, index) => (
        <StarIcon key={`empty-${index}`} index={fullStars + 1 + index} fillPercentage={0} />
      ))}
    </div>
  )
}
